import { useCallback, useEffect, useState, useMemo } from "react";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { R_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
// import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  getRajasthanMonthlySurplusDeficit,
  getRajasthanDailySurplusDeficit,
} from "../Utility/RestApi";
import { formatDateForAPI } from "../Utility/const";

const X_AXIS = [
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

// Helper function to parse month-year string back to comparable values
function parseMonthYear(monthYearStr: string | number | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) {
    return { month: 0, year: 0 };
  }

  if (typeof monthYearStr === "number") {
    return { month: monthYearStr, year: 0 };
  }

  if (typeof monthYearStr === "string") {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const parts = monthYearStr.split("-");
    if (parts.length !== 2) {
      return { month: 0, year: 0 };
    }
    const [monthStr, yearStr] = parts;
    const month = monthNames.indexOf(monthStr) + 1;
    const year = 2000 + parseInt(yearStr);
    return { month, year };
  }

  return { month: 0, year: 0 };
}

const GEN_BY = [
  {
    label: "Cumulative",
    keys: [
      {
        key: "drawal",
        label: "Drawal",
        stackId: "drawal",
      },
      {
        key: "total",
        label: "Total Gen. + Proc.",
        stackId: "gen",
      },
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
    ],
  },
  {
    label: "Comparative",
    keys: [
      {
        key: "drawal",
        label: "Drawal",
        stackId: "drawal",
      },
      {
        key: "total",
        label: "Total Gen. + Proc.",
        stackId: "gen",
      },
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
    ],
  },
];

const DISCOM = [
  {
    label: "JDVVNL",
    key: "JDVVNL",
  },
  {
    label: "JVVNL",
    key: "JVVNL",
  },
  {
    label: "AVVNL",
    key: "AVVNL",
  },
];

interface RajasthanForecastPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  genGroupBy: any;
  onGenGroupByChange: (index: number) => void;
}

export default function RajasthanForecastPeakAnalysis({
  onXAxisModeChange,
  selectedDiscom,
  onDiscomChange,
  genGroupBy,
  onGenGroupByChange,
}: RajasthanForecastPeakAnalysisProps) {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [data, setData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      if (DefaultDates[activeTab].defaultRangeDate === undefined) {
        return;
      }
      const dates = DefaultDates[activeTab].defaultRangeDate;

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      console.log("Fetching surplus/deficit data with dates:", {
        startDateStr,
        endDateStr,
      });
      console.log("Selected interval:", selectedXaxis.key);

      let allData: any[] = [];

      if (selectedXaxis.key === "month") {
        // Fetch monthly surplus/deficit data
        const monthlyData = await getRajasthanMonthlySurplusDeficit(
          startDateStr,
          endDateStr,
        );

        if (Array.isArray(monthlyData)) {
          // Format month field for display
          const formattedData = monthlyData.map((item) => {
            const monthNames = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];
            const yearShort = item.year.toString().slice(-2);
            const monthFormatted = `${monthNames[item.month - 1]}-${yearShort}`;

            return {
              ...item,
              month: monthFormatted,
              monthNumber: item.month,
            };
          });

          console.log("Monthly data fetched:", formattedData.length, "items");
          allData = formattedData;
        }
      } else {
        // Daily data
        console.log("Calling Daily Surplus/Deficit API...");
        const dailyData = await getRajasthanDailySurplusDeficit(
          startDateStr,
          endDateStr,
        );

        if (Array.isArray(dailyData)) {
          console.log("Daily data fetched:", dailyData.length, "items");

          allData = dailyData.map((item) => {
            // Extract morning (index 0 usually) and evening (index 1 usually) data
            // Based on RestApi implementation structure
            const morningData =
              item.formatted_data?.find(
                (d: any) => d.time_period === "Morning",
              ) || {};
            const eveningData =
              item.formatted_data?.find(
                (d: any) => d.time_period === "Evening",
              ) || {};

            return {
              ...item,
              date: item.date,

              // Map daily peaks
              peak_drawal_mw: item.daily_peak_drawal_mw,
              peak_total_gen_proc_mw: item.daily_peak_total_gen_proc_mw,
              peak_surplus_deficit_mw: item.daily_peak_surplus_deficit_mw,
              peak_surplus_deficit_pct: item.daily_peak_surplus_deficit_pct,

              // Map Morning peaks
              morning_peak_drawal_mw: morningData.max_drawal,
              morning_peak_total_gen_proc_mw: morningData.max_total_gen_proc,
              morning_peak_surplus_deficit_mw: morningData.max_surplus_deficit,
              morning_peak_surplus_deficit_pct:
                morningData.max_surplus_deficit_pct,

              // Map Evening peaks
              evening_peak_drawal_mw: eveningData.max_drawal,
              evening_peak_total_gen_proc_mw: eveningData.max_total_gen_proc,
              evening_peak_surplus_deficit_mw: eveningData.max_surplus_deficit,
              evening_peak_surplus_deficit_pct:
                eveningData.max_surplus_deficit_pct,
            };
          });
        }
      }

      setData(allData);

      // Set unique dates/months for filter
      if (selectedXaxis.key === "date") {
        setUniqueDate(
          Array.from(
            new Set(
              allData
                .map((item: any) => item.date)
                .filter((date: any) => date !== undefined)
                .sort(
                  (a: string, b: string) =>
                    new Date(a).getTime() - new Date(b).getTime(),
                ),
            ),
          ),
        );
      } else {
        const uniqueMonths = Array.from(
          new Set(
            allData
              .map((item: any) => item.month)
              .filter((month: any) => month !== undefined),
          ),
        ).sort((a: string, b: string) => {
          const aMonthYear = parseMonthYear(a);
          const bMonthYear = parseMonthYear(b);
          if (aMonthYear.year !== bMonthYear.year) {
            return aMonthYear.year - bMonthYear.year;
          }
          return aMonthYear.month - bMonthYear.month;
        });
        setUniqueDate(uniqueMonths);
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedXaxis.key]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    if (data.length === 0) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data;

    // Filter by month/date
    if (selectedDate.length > 0) {
      if (selectedXaxis.key === "date") {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.date),
        );
      } else {
        filteredData = filteredData.filter((item) =>
          selectedDate.map((d) => d.value).includes(item.month),
        );
      }
    }

    // Sort
    filteredData.sort((a, b) => {
      if (selectedXaxis.key === "date") {
        if (!a.date || !b.date) return 0;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      } else {
        if (!a.month || !b.month) return 0;
        const aVal = parseMonthYear(a.month);
        const bVal = parseMonthYear(b.month);
        if (aVal.year !== bVal.year) return aVal.year - bVal.year;
        return aVal.month - bVal.month;
      }
    });

    // Map API fields to Generic Keys based on GEN_BY selection
    // The keys used in GEN_BY are: "deficit", "total"

    // Determine which keys to process based on active Gen By tab
    const activeGenBy =
      GEN_BY.find((g) => g.label === genGroupBy.label) || GEN_BY[0];
    const metricsToProcess = activeGenBy.keys.map((k) => k.key);

    // Create chart-ready data directly
    const processedChartData = filteredData.map((item) => {
      const baseData: any = {
        ...item,
        // Ensure month/year are available for x-axis
        month: item.month,
        year: item.year,
        date: item.date,
      };

      // Map API columns to the generic keys
      // We use simple keys that ReMixChart can access directly

      if (metricsToProcess.includes("drawal")) {
        baseData[`drawal_peak`] = item.peak_drawal_mw;
        baseData[`drawal_morning_peak`] = item.morning_peak_drawal_mw;
        baseData[`drawal_evening_peak`] = item.evening_peak_drawal_mw;
      }

      if (metricsToProcess.includes("deficit")) {
        baseData[`deficit_peak`] = item.peak_surplus_deficit_mw;
        baseData[`deficit_morning_peak`] = item.morning_peak_surplus_deficit_mw;
        baseData[`deficit_evening_peak`] = item.evening_peak_surplus_deficit_mw;
      }

      if (metricsToProcess.includes("total")) {
        baseData[`total_peak`] = item.peak_total_gen_proc_mw;
        baseData[`total_morning_peak`] = item.morning_peak_total_gen_proc_mw;
        baseData[`total_evening_peak`] = item.evening_peak_total_gen_proc_mw;
      }

      if (metricsToProcess.includes("deficit_percent")) {
        // Percentage metrics
        baseData[`deficit_percent_peak`] = item.peak_surplus_deficit_pct;
        baseData[`deficit_percent_peak_date`] =
          item.date_of_peak_surplus_deficit_pct;
        baseData[`deficit_percent_peak_block`] =
          item.block_of_peak_surplus_deficit_pct;

        baseData[`deficit_percent_morning_peak`] =
          item.morning_peak_surplus_deficit_pct;
        baseData[`deficit_percent_morning_peak_date`] =
          item.date_of_morning_peak_surplus_deficit_pct;
        baseData[`deficit_percent_morning_peak_block`] =
          item.block_of_morning_peak_surplus_deficit_pct;

        baseData[`deficit_percent_evening_peak`] =
          item.evening_peak_surplus_deficit_pct;
        baseData[`deficit_percent_evening_peak_date`] =
          item.date_of_evening_peak_surplus_deficit_pct;
        baseData[`deficit_percent_evening_peak_block`] =
          item.block_of_evening_peak_surplus_deficit_pct;
      }

      return baseData;
    });

    // For FormatChartData (used primarily for Table), we still need to pass structure
    // But for the Chart itself, we can use the processed data directly if we set keys right

    let aggregationKeys: string[] = [];
    metricsToProcess.forEach((key) => {
      aggregationKeys.push(`${key}_peak`);
      aggregationKeys.push(`${key}_morning_peak`);
      aggregationKeys.push(`${key}_evening_peak`);
    });

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys: selectedXaxis.key === "date" ? ["date"] : ["month", "year"],
      groupBy: [],
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: processedChartData,
    };

    const cData = FormatChartData(par);

    // For the Chart, use the processed data directly but we need keys for getLegends
    // We'll trust our manual processing for chart rendering stability
    setTableData(cData.tableData || []);
    setChartData(processedChartData); // Use processed data directly for chart

    // Set chart keys to the metric keys (e.g. ["deficit", "total"]) to drive legends
    setChartKeys(metricsToProcess);
  }, [data, selectedXaxis, selectedDate, genGroupBy]);

  const legends = useMemo(() => {
    // ✅ Standard Peak Analysis Legends
    const peakTypes = [
      { field: "peak", displayName: "Day Peak" },
      { field: "morning_peak", displayName: "Morning Peak" },
      { field: "evening_peak", displayName: "Evening Peak" },
    ];

    const legendsArray: any[] = [];

    // ✅ Find active GEN_BY config only once
    const activeGenBy =
      GEN_BY.find((g) => g.label === genGroupBy.label) || GEN_BY[0];

    // ✅ Iterate over GEN_BY keys
    activeGenBy.keys.forEach((metricConfig) => {
      const metricKey = metricConfig.key; // e.g. "deficit"
      const metricLabel = metricConfig.label; // e.g. "Surplus/Deficit"

      // Skip if metric not in chartKeys
      if (!chartKeys.includes(metricKey)) return;

      // ✅ Create peak legends
      peakTypes.forEach((peak) => {
        legendsArray.push({
          dataKey: `${metricKey}_${peak.field}`,
          name: `${metricLabel} ${peak.displayName}`,

          legendColor: undefined,
          stroke: undefined,
          type: selectedChartType.value,

          stackId:
            selectedChartType.stackId !== undefined ? peak.field : undefined,
        });
      });
    });

    return legendsArray;
  }, [chartKeys, genGroupBy, selectedChartType]);


  function summaryExtraKeys() {
    if (selectedXaxis.key === "date") {
      return ["date"];
    } else {
      return ["month", "year"];
    }
  }

  const summaryExtraLabels =
    selectedXaxis.key === "date" ? ["Date"] : ["Month", "Year"];

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    unit: "MW",
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Forecast Surplus Deficit Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
              setSelectedDate([]);
            }}
          />
          <FilterMenu
            title="Generation By"
            items={GEN_BY.map((item) => item.label)}
            selectedItems={[genGroupBy.label]}
            onSelect={(item, index) => onGenGroupByChange(index)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={onDiscomChange}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-100">Forecast Peak Analysis (MW)</h2>
            <div className="flex gap-x-6 justify-end">
              {/* <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              /> */}
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(tablePar);
                  } else if (dataTab.label === "Summary") {
                    downloadSummaryCSV(tablePar);
                  } else if (dataTab.label === "Table") {
                    downloadChartTableCSV(tablePar);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={undefined}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit="MW"
              yAxisLabel="MW"
              legends={legends}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey={selectedXaxis.values[0]}
              secondXDataKey={undefined}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Monthly Surplus/Deficit Analysis</p>}
              text="1. Drawal: Actual total drawal from the grid
2. Total Gen. + Proc.: Total generation plus procurement
3. Surplus/Deficit: Difference between total generation/procurement and drawal
4. Peak values represent the maximum values during the month for day, morning (00:00-12:00), and evening (12:00-24:00) periods."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
